import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { serviceQuestionKeys } from './query-key-factory'

import { MutationConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { Enums } from '@/lib/types/supabase'
import { logger } from '@/logger'

export type ServiceQuestionType = Enums<'question_type_enum'>

export const createQuestionInputSchema = z.object({
  type: z.custom<ServiceQuestionType>(),
  text: z.string().min(1, 'Le titre ne peut pas être vide').max(70, 'Le titre doit contenir au plus 70 caractères'),
  choices: z.array(z.string()).optional(),
})

export type CreateQuestionInput = z.infer<typeof createQuestionInputSchema>

export const createServiceQuestion = async ({ tid, values }: { tid: string; values: CreateQuestionInput }) => {
  const { data: question, error: questionError } = await supabase
    .from('questions')
    .insert({ technician_id: tid, type: values.type, text: values.text })
    .select('id')
    .single()

  if (questionError || !question) {
    logger.error('Failed to create service question', {
      errorMessage: questionError,
      values,
    })
    throw questionError
  }

  const choices = values.choices?.filter((choice) => choice.trim().length > 0) ?? []

  if (choices.length > 0 && values.type !== 'open') {
    const { error: choicesError } = await supabase
      .from('question_choices')
      .insert(choices.map((choice_text) => ({ question_id: question.id, choice_text })))

    if (choicesError) {
      logger.error('Failed to create question choices', {
        errorMessage: choicesError,
        choices,
      })
      throw choicesError
    }
  }

  return question
}

type UseCreateServiceQuestionOptions = {
  tid: string
  mutationConfig?: MutationConfig<typeof createServiceQuestion>
}

export const useCreateServiceQuestion = ({ tid, mutationConfig }: UseCreateServiceQuestionOptions) => {
  const queryClient = useQueryClient()

  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    onSuccess: (...args) => {
      queryClient.refetchQueries({
        queryKey: serviceQuestionKeys.lists(),
      })
      onSuccess?.(...args)
    },
    ...restConfig,
    mutationFn: ({ values }: { values: CreateQuestionInput }) => createServiceQuestion({ tid, values }),
  })
}
